/**
 * Capacitor Native Features Service
 * Portal de Gastos Personales
 * 
 * Este servicio encapsula todas las funcionalidades nativas de Capacitor
 * para Android e iOS
 */

import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Network } from '@capacitor/network';
import { Keyboard } from '@capacitor/keyboard';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Clipboard } from '@capacitor/clipboard';
import { Toast } from '@capacitor/toast';
import { Share } from '@capacitor/share';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';

class CapacitorService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'
  }

  /**
   * Verifica si estamos en una plataforma nativa
   */
  isNativePlatform() {
    return this.isNative;
  }

  /**
   * Obtiene la plataforma actual
   */
  getPlatform() {
    return this.platform;
  }

  // ============================================
  // CAMERA - Tomar fotos y acceder a la galería
  // ============================================

  /**
   * Toma una foto con la cámara
   * @returns {Promise<string>} Base64 de la imagen
   */
  async takePicture() {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera
      });
      
      return `data:image/${image.format};base64,${image.base64String}`;
    } catch (error) {
      console.error('Error al tomar foto:', error);
      throw new Error('No se pudo tomar la foto');
    }
  }

  /**
   * Selecciona una imagen de la galería
   * @returns {Promise<string>} Base64 de la imagen
   */
  async pickImage() {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Photos
      });
      
      return `data:image/${image.format};base64,${image.base64String}`;
    } catch (error) {
      console.error('Error al seleccionar imagen:', error);
      throw new Error('No se pudo seleccionar la imagen');
    }
  }

  /**
   * Permite elegir entre cámara o galería
   * @returns {Promise<string>} Base64 de la imagen
   */
  async chooseImage() {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Base64,
        source: CameraSource.Prompt, // Muestra prompt de selección
        promptLabelHeader: 'Seleccionar imagen',
        promptLabelCancel: 'Cancelar',
        promptLabelPhoto: 'Desde galería',
        promptLabelPicture: 'Tomar foto'
      });
      
      return `data:image/${image.format};base64,${image.base64String}`;
    } catch (error) {
      console.error('Error al elegir imagen:', error);
      throw new Error('No se pudo obtener la imagen');
    }
  }

  // ============================================
  // GEOLOCATION - Ubicación GPS
  // ============================================

  /**
   * Obtiene la ubicación actual del dispositivo
   * @returns {Promise<Object>} {latitude, longitude, accuracy, timestamp}
   */
  async getCurrentLocation() {
    try {
      const coordinates = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });
      
      return {
        latitude: coordinates.coords.latitude,
        longitude: coordinates.coords.longitude,
        accuracy: coordinates.coords.accuracy,
        timestamp: coordinates.timestamp
      };
    } catch (error) {
      console.error('Error al obtener ubicación:', error);
      throw new Error('No se pudo obtener la ubicación');
    }
  }

  /**
   * Verifica si los permisos de ubicación están otorgados
   */
  async checkLocationPermissions() {
    try {
      const permission = await Geolocation.checkPermissions();
      return permission.location === 'granted';
    } catch (error) {
      console.error('Error al verificar permisos de ubicación:', error);
      return false;
    }
  }

  /**
   * Solicita permisos de ubicación
   */
  async requestLocationPermissions() {
    try {
      const permission = await Geolocation.requestPermissions();
      return permission.location === 'granted';
    } catch (error) {
      console.error('Error al solicitar permisos de ubicación:', error);
      return false;
    }
  }

  // ============================================
  // HAPTICS - Feedback táctil
  // ============================================

  /**
   * Genera una vibración ligera (para toques en botones)
   */
  async hapticsLight() {
    if (!this.isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
      console.error('Error en haptics:', error);
    }
  }

  /**
   * Genera una vibración media (para acciones importantes)
   */
  async hapticsMedium() {
    if (!this.isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch (error) {
      console.error('Error en haptics:', error);
    }
  }

  /**
   * Genera una vibración fuerte (para alertas)
   */
  async hapticsHeavy() {
    if (!this.isNative) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch (error) {
      console.error('Error en haptics:', error);
    }
  }

  /**
   * Vibración para éxito
   */
  async hapticsSuccess() {
    if (!this.isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Success });
    } catch (error) {
      console.error('Error en haptics:', error);
    }
  }

  /**
   * Vibración para advertencia
   */
  async hapticsWarning() {
    if (!this.isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Warning });
    } catch (error) {
      console.error('Error en haptics:', error);
    }
  }

  /**
   * Vibración para error
   */
  async hapticsError() {
    if (!this.isNative) return;
    try {
      await Haptics.notification({ type: NotificationType.Error });
    } catch (error) {
      console.error('Error en haptics:', error);
    }
  }

  // ============================================
  // STATUS BAR - Barra de estado
  // ============================================

  /**
   * Configura el estilo de la barra de estado
   * @param {string} style - 'light' o 'dark'
   */
  async setStatusBarStyle(style = 'dark') {
    if (!this.isNative) return;
    try {
      await StatusBar.setStyle({ style: style === 'light' ? Style.Light : Style.Dark });
    } catch (error) {
      console.error('Error al configurar status bar:', error);
    }
  }

  /**
   * Configura el color de fondo de la barra de estado (solo Android)
   * @param {string} color - Código hexadecimal del color
   */
  async setStatusBarColor(color) {
    if (!this.isNative || this.platform === 'ios') return;
    try {
      await StatusBar.setBackgroundColor({ color });
    } catch (error) {
      console.error('Error al configurar color de status bar:', error);
    }
  }

  /**
   * Oculta la barra de estado
   */
  async hideStatusBar() {
    if (!this.isNative) return;
    try {
      await StatusBar.hide();
    } catch (error) {
      console.error('Error al ocultar status bar:', error);
    }
  }

  /**
   * Muestra la barra de estado
   */
  async showStatusBar() {
    if (!this.isNative) return;
    try {
      await StatusBar.show();
    } catch (error) {
      console.error('Error al mostrar status bar:', error);
    }
  }

  // ============================================
  // SPLASH SCREEN - Pantalla de carga
  // ============================================

  /**
   * Oculta el splash screen
   */
  async hideSplashScreen() {
    if (!this.isNative) return;
    try {
      await SplashScreen.hide();
    } catch (error) {
      console.error('Error al ocultar splash screen:', error);
    }
  }

  /**
   * Muestra el splash screen
   */
  async showSplashScreen() {
    if (!this.isNative) return;
    try {
      await SplashScreen.show({
        autoHide: false,
        fadeOutDuration: 1000
      });
    } catch (error) {
      console.error('Error al mostrar splash screen:', error);
    }
  }

  // ============================================
  // FILESYSTEM - Sistema de archivos
  // ============================================

  /**
   * Guarda un archivo en el dispositivo
   * @param {string} fileName - Nombre del archivo
   * @param {string} data - Contenido del archivo (base64 o texto)
   * @param {string} directory - Directorio donde guardar
   * @returns {Promise<string>} URI del archivo guardado
   */
  async saveFile(fileName, data, directory = Directory.Documents) {
    try {
      const result = await Filesystem.writeFile({
        path: fileName,
        data: data,
        directory: directory,
        recursive: true
      });
      
      return result.uri;
    } catch (error) {
      console.error('Error al guardar archivo:', error);
      throw new Error('No se pudo guardar el archivo');
    }
  }

  /**
   * Lee un archivo del dispositivo
   * @param {string} fileName - Nombre del archivo
   * @param {string} directory - Directorio donde leer
   * @returns {Promise<string>} Contenido del archivo
   */
  async readFile(fileName, directory = Directory.Documents) {
    try {
      const result = await Filesystem.readFile({
        path: fileName,
        directory: directory
      });
      
      return result.data;
    } catch (error) {
      console.error('Error al leer archivo:', error);
      throw new Error('No se pudo leer el archivo');
    }
  }

  /**
   * Elimina un archivo del dispositivo
   * @param {string} fileName - Nombre del archivo
   * @param {string} directory - Directorio del archivo
   */
  async deleteFile(fileName, directory = Directory.Documents) {
    try {
      await Filesystem.deleteFile({
        path: fileName,
        directory: directory
      });
    } catch (error) {
      console.error('Error al eliminar archivo:', error);
      throw new Error('No se pudo eliminar el archivo');
    }
  }

  // ============================================
  // NETWORK - Estado de la red
  // ============================================

  /**
   * Obtiene el estado actual de la red
   * @returns {Promise<Object>} {connected, connectionType}
   */
  async getNetworkStatus() {
    try {
      const status = await Network.getStatus();
      return {
        connected: status.connected,
        connectionType: status.connectionType // 'wifi', 'cellular', 'none', 'unknown'
      };
    } catch (error) {
      console.error('Error al obtener estado de red:', error);
      return { connected: true, connectionType: 'unknown' };
    }
  }

  /**
   * Registra un listener para cambios en el estado de la red
   * @param {Function} callback - Función a ejecutar cuando cambie el estado
   * @returns {Promise<PluginListenerHandle>} Handle para remover el listener
   */
  async onNetworkChange(callback) {
    try {
      const handler = await Network.addListener('networkStatusChange', (status) => {
        callback({
          connected: status.connected,
          connectionType: status.connectionType
        });
      });
      return handler;
    } catch (error) {
      console.error('Error al registrar listener de red:', error);
      return null;
    }
  }

  // ============================================
  // KEYBOARD - Teclado nativo
  // ============================================

  /**
   * Oculta el teclado
   */
  async hideKeyboard() {
    if (!this.isNative) return;
    try {
      await Keyboard.hide();
    } catch (error) {
      console.error('Error al ocultar teclado:', error);
    }
  }

  /**
   * Muestra el teclado
   */
  async showKeyboard() {
    if (!this.isNative) return;
    try {
      await Keyboard.show();
    } catch (error) {
      console.error('Error al mostrar teclado:', error);
    }
  }

  /**
   * Registra un listener para cuando el teclado aparece
   * @param {Function} callback - Función a ejecutar
   */
  async onKeyboardShow(callback) {
    if (!this.isNative) return null;
    try {
      const handler = await Keyboard.addListener('keyboardWillShow', (info) => {
        callback(info);
      });
      return handler;
    } catch (error) {
      console.error('Error al registrar listener de teclado:', error);
      return null;
    }
  }

  /**
   * Registra un listener para cuando el teclado desaparece
   * @param {Function} callback - Función a ejecutar
   */
  async onKeyboardHide(callback) {
    if (!this.isNative) return null;
    try {
      const handler = await Keyboard.addListener('keyboardWillHide', () => {
        callback();
      });
      return handler;
    } catch (error) {
      console.error('Error al registrar listener de teclado:', error);
      return null;
    }
  }

  // ============================================
  // LOCAL NOTIFICATIONS - Notificaciones locales
  // ============================================

  /**
   * Solicita permisos para notificaciones locales
   */
  async requestNotificationPermissions() {
    try {
      const result = await LocalNotifications.requestPermissions();
      return result.display === 'granted';
    } catch (error) {
      console.error('Error al solicitar permisos de notificaciones:', error);
      return false;
    }
  }

  /**
   * Programa una notificación local
   * @param {Object} notification - {title, body, id, schedule}
   */
  async scheduleNotification({ title, body, id, schedule }) {
    try {
      await LocalNotifications.schedule({
        notifications: [
          {
            title: title,
            body: body,
            id: id || Date.now(),
            schedule: schedule, // { at: new Date(Date.now() + 1000 * 5) }
            sound: 'beep.wav',
            attachments: null,
            actionTypeId: '',
            extra: null
          }
        ]
      });
    } catch (error) {
      console.error('Error al programar notificación:', error);
      throw new Error('No se pudo programar la notificación');
    }
  }

  /**
   * Muestra una notificación local inmediata
   * @param {string} title - Título de la notificación
   * @param {string} body - Cuerpo de la notificación
   */
  async showNotification(title, body) {
    try {
      await this.scheduleNotification({
        title,
        body,
        id: Date.now(),
        schedule: { at: new Date(Date.now() + 1000) } // 1 segundo
      });
    } catch (error) {
      console.error('Error al mostrar notificación:', error);
    }
  }

  /**
   * Cancela una notificación programada
   * @param {number} id - ID de la notificación
   */
  async cancelNotification(id) {
    try {
      await LocalNotifications.cancel({ notifications: [{ id }] });
    } catch (error) {
      console.error('Error al cancelar notificación:', error);
    }
  }

  // ============================================
  // CLIPBOARD - Portapapeles
  // ============================================

  /**
   * Copia texto al portapapeles
   * @param {string} text - Texto a copiar
   */
  async copyToClipboard(text) {
    try {
      await Clipboard.write({
        string: text
      });
      await this.showToast('Copiado al portapapeles');
    } catch (error) {
      console.error('Error al copiar al portapapeles:', error);
      throw new Error('No se pudo copiar al portapapeles');
    }
  }

  /**
   * Lee el contenido del portapapeles
   * @returns {Promise<string>} Texto del portapapeles
   */
  async readFromClipboard() {
    try {
      const { value } = await Clipboard.read();
      return value;
    } catch (error) {
      console.error('Error al leer del portapapeles:', error);
      return '';
    }
  }

  // ============================================
  // TOAST - Mensajes emergentes
  // ============================================

  /**
   * Muestra un mensaje toast
   * @param {string} text - Texto del mensaje
   * @param {string} duration - 'short' o 'long'
   * @param {string} position - 'top', 'center', 'bottom'
   */
  async showToast(text, duration = 'short', position = 'bottom') {
    try {
      await Toast.show({
        text: text,
        duration: duration,
        position: position
      });
    } catch (error) {
      console.error('Error al mostrar toast:', error);
    }
  }

  // ============================================
  // SHARE - Compartir contenido
  // ============================================

  /**
   * Comparte texto o URL
   * @param {Object} options - {title, text, url, dialogTitle}
   */
  async share({ title, text, url, dialogTitle }) {
    try {
      await Share.share({
        title: title,
        text: text,
        url: url,
        dialogTitle: dialogTitle || 'Compartir'
      });
    } catch (error) {
      console.error('Error al compartir:', error);
      throw new Error('No se pudo compartir');
    }
  }

  /**
   * Verifica si la función de compartir está disponible
   */
  async canShare() {
    try {
      const result = await Share.canShare();
      return result.value;
    } catch (error) {
      return false;
    }
  }
  // ============================================
  // PUSH NOTIFICATIONS - Notificaciones Push (FCM)
  // ============================================

  /**
   * Solicita permisos para notificaciones push y registra el dispositivo
   * @returns {Promise<string|null>} El token de registro (FCM) o null si falla
   */
  async registerPushNotifications() {
    if (!this.isNative) return null;

    try {
      let permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        console.warn('⚠️ Permiso de notificaciones no otorgado');
        return null;
      }

      // IMPORTANTE: Primero removemos listeners previos para evitar duplicados
      await this.removePushListeners();

      // Creamos la promesa antes de registrar
      const tokenPromise = new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          resolve(null); // Timeout tras 10 segundos
        }, 10000);

        PushNotifications.addListener('registration', (token) => {
          clearTimeout(timeout);
          console.log('✅ Token de registro exitoso:', token.value);
          resolve(token.value);
        });

        PushNotifications.addListener('registrationError', (err) => {
          clearTimeout(timeout);
          console.error('❌ Error en registro de notificaciones:', err.error);
          resolve(null);
        });
      });

      // Disparamos el registro nativo
      await PushNotifications.register();
      
      return tokenPromise;
    } catch (error) {
      console.error('Error al configurar notificaciones push:', error);
      return null;
    }
  }

  /**
   * Agrega listeners para eventos de notificaciones push
   * @param {Object} handlers - { onReceived, onAction }
   */
  async addPushListeners(handlers) {
    if (!this.isNative) return;

    try {
      if (handlers.onReceived) {
        await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('🔔 Notificación recibida:', notification);
          handlers.onReceived(notification);
        });
      }

      if (handlers.onAction) {
        await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('👆 Acción en notificación:', notification);
          handlers.onAction(notification);
        });
      }
    } catch (error) {
      console.error('Error al agregar listeners de push:', error);
    }
  }

  /**
   * Elimina todos los listeners de notificaciones push
   */
  async removePushListeners() {
    if (!this.isNative) return;
    try {
      await PushNotifications.removeAllListeners();
    } catch (error) {
      console.error('Error al eliminar listeners de push:', error);
    }
  }
}

// Exportar una instancia única del servicio
const capacitorService = new CapacitorService();
export default capacitorService;
